<script lang="ts" setup>
import { useVuelidate } from '@vuelidate/core';
import { maxValue, minValue, required } from '@vuelidate/validators';
import { computed, inject, nextTick, onBeforeUnmount, reactive, ref, toRefs, watch, type ComputedRef, type PropType, type Ref } from 'vue';
import { useI18n } from 'vue-i18n';

import BigNumber from '@exchange/helpers/bignumber';
import { trimNumberToPrecision } from '@exchange/helpers/number-transform-helper';
import { accountService } from '@exchange/libs/account/service/src';
import { authService } from '@exchange/libs/auth/service/src';
import { displayDefaultFailedToast } from '@exchange/libs/composables/shared/src/lib/usePromise';
import { futuresService } from '@exchange/libs/futures/service/src';
import type { MarketModel } from '@exchange/libs/market/service/src';
import { MarketState } from '@exchange/libs/market/service/src';
import { orderformService } from '@exchange/libs/order/order-form/service/src';
import { OrderSide, OrderType, TimeInForceType } from '@exchange/libs/order/shared-model/src/lib/order-essentials';
import { StaticOrderbook } from '@exchange/libs/orderbook/service/src';
import PublicRest from '@exchange/libs/rest-api/public-api';
import { settingsService } from '@exchange/libs/settings/service/src';
import { CONSTANTS } from '@exchange/libs/utils/constants/src';
import { currencyService } from '@exchange/libs/utils/currency/src';
import { GACategory, GAOrderAction, gaService } from '@exchange/libs/utils/google-analytics/src';
import { logger } from '@exchange/libs/utils/simple-logger/src';

import { type WarningModalsOrderBook } from './check-order-price';
import OrderFormBalance from './components/OrderFormBalance.vue';
import OrderFormFees from './components/OrderFormFees.vue';
import OrderFormSettings from './components/OrderFormSettings.vue';
import OrderFormStatusMessage from './components/OrderFormStatusMessage.vue';
import OrderSizeInput from './components/OrderSizeInput.vue';
import { getMinAmount } from './helpers';
import { type OrderFormEmitterType } from './order-form-event-bus';
import useOrderTypes from './OrderTypes';
import useOrderFormComputedHelpers from './useOrderFormComputedHelpers';
import useOrderFormPrice from './useOrderFormPrice';
import useOrderFormSlider from './useOrderFormSlider';
import useWarningModals from './useWarningModals';

const DEFAULT_MAX_LEVERAGE_LOGGED_OUT_DISPLAY_VALUE = 10;

const props = defineProps({
  balanceLoadingUntil: { type: Boolean, required: true },
  fitSliderTooltipMaxValue: { type: Boolean, required: false },
  psScrollTriggered: { type: Number, required: false },
  spotMarket: { type: Object as PropType<MarketModel>, required: true },
  isFutures: { type: Boolean, required: false, default: false },
});

interface ValidationStateInterface {
  amountValue: Ref<string | null>;
  limitPriceValue: Ref<string | null>;
  stopPriceValue: Ref<string | null>;
  totalValue: Ref<string | null>;
  currentMinAmount: ComputedRef<string>;
  currentMinAmountForMarketOrder: Ref<string>;
  currentMinPrice: ComputedRef<string>;
  currentMaxPrice: ComputedRef<string>;
  market: Ref<MarketModel>;
}

const getOrderBook = async ({ marketPrecision, marketId }): Promise<WarningModalsOrderBook> => {
  if (!marketId) {
    throw new Error('There is no market');
  }

  try {
    const orderBookSnapshot = await PublicRest.OrderBook.getInstrument(marketId);

    return new StaticOrderbook(marketPrecision, orderBookSnapshot);
  } catch (e) {
    logger.warn('Obtaining OrderBook REST call failed:', e);
    throw e;
  }
};

const useTooltips = (market: Ref<MarketModel>) => {
  const { currencies, defaultFiatCurrency } = currencyService;

  const showTooltips = ref({
    limitPriceValue: false,
    stopPriceValue: false,
    amountValue: false,
    totalValue: false,
  });

  const resetShowTooltips = () => {
    showTooltips.value = {
      limitPriceValue: false,
      stopPriceValue: false,
      amountValue: false,
      totalValue: false,
    };
  };

  const tooltipContent = (inValue: string | null, inputName: 'stopPriceValue' | 'limitPriceValue' | 'amountValue' | 'totalValue') => {
    const value = Number(inValue);

    if (!currencies.value || !market.value || !value) {
      return null;
    }

    if (inputName !== 'amountValue' && market.value.quote.code === defaultFiatCurrency.value) {
      return null;
    }

    const defaultFiatCurrencyPrecision = currencies.value[defaultFiatCurrency.value]?.precision ?? CONSTANTS.PRECISION.DEFAULT_FIAT;
    const inputCurrencyInFiatValue = currencies.value[market.value[inputName === 'amountValue' ? 'base' : 'quote'].code]?.defaultFiatValue || 0;
    let fiatValue: string | number = value * inputCurrencyInFiatValue;

    fiatValue = trimNumberToPrecision(defaultFiatCurrencyPrecision)(fiatValue);

    return {
      content: `${fiatValue} ${defaultFiatCurrency.value}`,
      theme: 'tooltip-fiat',
      shown: showTooltips.value[inputName],
      placement: 'top-end',
      distance: 0.4,
    };
  };

  const tooltipFormatter = (maxAmountForSlider: ComputedRef<number>) => (value) => {
    const percent = (100 * value) / maxAmountForSlider.value;

    return `${percent.toFixed()}%`;
  };

  return {
    showTooltips,
    resetShowTooltips,
    tooltipContent,
    tooltipFormatter,
  };
};

const useInputsValidation = (
  amountValue: Ref<string | null>,
  limitPriceValue: Ref<string | null>,
  stopPriceValue: Ref<string | null>,
  totalValue: Ref<string | null>,
  market: Ref<MarketModel>,
  currentMinAmountForMarketOrder: Ref<string>,
  currentMinPrice: ComputedRef<string>,
  currentMaxPrice: ComputedRef<string>,
) => {
  const { t, te } = useI18n({ useScope: 'global' });

  const touchMap = new Map();
  const storedType = computed(() => orderformService.data.type);
  const submitStatus = computed(() => orderformService.data.submitStatus);
  const submitStatusMessage = computed(() => {
    if (!submitStatus.value.code) {
      return undefined;
    }

    const tKey = `modules.orderForm.errors.${submitStatus.value.code}`;

    return te(tKey) ? t(tKey, { market: market.value.name }) : t('modules.orderForm.errors.default');
  });
  const currentMinAmount = computed(() => {
    const { amountPrecision, minSize } = market.value;

    const configMin = getMinAmount({ amountPrecision });
    const min = limitPriceValue.value && Number(limitPriceValue.value) !== 0 ? new BigNumber(minSize).div(limitPriceValue.value) : configMin;

    return min.toFixed(amountPrecision, 0);
  });
  const validationRules = computed(() => {
    const validation = {
      amountValue: { minAmount: minValue(currentMinAmount.value), required, $autoDirty: true },
      amountValueMarket: { minAmountForMarketOrder: minValue(currentMinAmountForMarketOrder.value), required, $autoDirty: true },
      limitPriceValue: {
        minPrice: minValue(currentMinPrice.value),
        maxPrice: maxValue(currentMaxPrice.value),
        required,
        $autoDirty: true,
      },
      stopPriceValue: {
        minPrice: minValue(currentMinPrice.value),
        maxPrice: maxValue(currentMaxPrice.value),
        required,
        $autoDirty: true,
      },
      totalValue: { positiveNumbersOnly: (s: string) => Number(s) > 0, $autoDirty: true },
    };
    const validationSwitch = reactive({
      MARKET: {
        amountValue: validation.amountValueMarket,
        totalValue: validation.totalValue,
      },
      LIMIT: {
        amountValue: validation.amountValue,
        limitPriceValue: validation.limitPriceValue,
        totalValue: validation.totalValue,
      },
      STOP: {
        amountValue: validation.amountValue,
        limitPriceValue: validation.limitPriceValue,
        stopPriceValue: validation.stopPriceValue,
        totalValue: validation.totalValue,
      },
      default: () => {
        logger.warn('no validation prop provided');

        return {};
      },
    });

    return validationSwitch[storedType.value] || validationSwitch.default();
  });

  const validationState = reactive<ValidationStateInterface>({
    amountValue,
    limitPriceValue,
    stopPriceValue,
    totalValue,
    currentMinAmount,
    currentMinAmountForMarketOrder,
    currentMinPrice,
    currentMaxPrice,
    market,
  });

  const v = useVuelidate(validationRules, validationState);
  const validationErrorMessages = computed(() => {
    const requiredMessage = t('modules.orderForm.validation.required');
    const quoteSymbol = market.value.quote.code;
    const baseSymbol = market.value.base.code;

    const validation = {
      amountValue: {
        minAmount: t('modules.orderForm.validation.minAmount', { minAmount: currentMinAmount.value, symbol: baseSymbol }),
        minAmountForMarketOrder: t('modules.orderForm.validation.minAmountForMarketOrder', { minAmountForMarketOrder: currentMinAmountForMarketOrder.value, symbol: baseSymbol }),
        required: requiredMessage,
      },
      limitPriceValue: {
        minPrice: t('modules.orderForm.validation.minPrice', { minPrice: currentMinPrice.value, symbol: quoteSymbol }),
        maxPrice: t('modules.orderForm.validation.maxPrice', { maxPrice: currentMaxPrice.value, symbol: quoteSymbol }),
        required: requiredMessage,
      },
      stopPriceValue: {
        minPrice: t('modules.orderForm.validation.minPrice', { minPrice: currentMinPrice.value, symbol: quoteSymbol }),
        maxPrice: t('modules.orderForm.validation.maxPrice', { maxPrice: currentMaxPrice.value, symbol: quoteSymbol }),
        required: requiredMessage,
      },
      totalValue: {
        positiveNumbersOnly: t('modules.orderForm.validation.positiveNumbersOnly'),
      },
    };

    return validation;
  });

  const vuelidateReset = () => {
    if (v.value.amountValue) {
      v.value.amountValue.$reset();
    }

    if (v.value.totalValue) {
      v.value.totalValue.$reset();
    }

    v.value.$reset();

    touchMap.forEach((el) => {
      clearTimeout(el); // reset timeouts in touch map as well
    });
  };

  return {
    v,
    validationErrorMessages,
    vuelidateReset,
    currentMinAmount,
    currentMinPrice,
    currentMaxPrice,
    currentMinAmountForMarketOrder,
    submitStatus,
    submitStatusMessage,
  };
};

const useFormInputs = (market: Ref<MarketModel>, currentMinAmountForMarketOrder: Ref<string>, currentMinPrice: ComputedRef<string>, currentMaxPrice: ComputedRef<string>) => {
  const stopPriceValue = ref<string | null>(null);
  const limitPriceValue = ref<string | null>(null);
  const amountValue = ref<string | null>(null);
  const totalValue = ref<string | null>(null);

  const { v, validationErrorMessages, vuelidateReset, currentMinAmount, submitStatus, submitStatusMessage } = useInputsValidation(
    amountValue,
    limitPriceValue,
    stopPriceValue,
    totalValue,
    market,
    currentMinAmountForMarketOrder,
    currentMinPrice,
    currentMaxPrice,
  );

  const defineValueToSaveToOrderformServiceBasedOnInput = (input: string, { allowZero = true } = {}) => {
    if (input === '') {
      return null;
    }

    const inputNumber = Number(input);

    if (inputNumber > (allowZero ? -1 : 0) && !Number.isNaN(inputNumber)) {
      return input;
    }

    if (inputNumber === 0) {
      return null;
    }

    return undefined;
  };

  const stopPriceInput = {
    set: ($eventValue: string) => {
      orderformService.setSubmitStatus({ reset: true });

      stopPriceValue.value = $eventValue;
      const orderformServiceValue = defineValueToSaveToOrderformServiceBasedOnInput($eventValue);

      if (orderformServiceValue !== undefined) {
        orderformService.setStopPrice(orderformServiceValue);
      }
    },
    onServiceChangeUpdate: (value: string | null) => {
      if (value !== stopPriceValue.value) {
        stopPriceValue.value = value;
      }
    },
  };

  const limitPriceInput = {
    set: ($eventValue: string) => {
      orderformService.setSubmitStatus({ reset: true });

      limitPriceValue.value = $eventValue;
      const newValue = defineValueToSaveToOrderformServiceBasedOnInput($eventValue);

      if (newValue !== undefined) {
        orderformService.setLimitPrice(newValue);
      }
    },
    onServiceChangeUpdate: (value: string | null) => {
      if (value !== limitPriceValue.value) {
        limitPriceValue.value = value;
      }
    },
  };

  const amountInput = {
    set: ($eventValue: string) => {
      orderformService.setSubmitStatus({ reset: true });

      amountValue.value = $eventValue;
      const newValue = defineValueToSaveToOrderformServiceBasedOnInput($eventValue);

      if (newValue !== undefined) {
        orderformService.setAmount(newValue);
      }
    },
    onServiceChangeUpdate: (value: string | null) => {
      if (value !== amountValue.value) {
        amountValue.value = value;
      }
    },
  };

  const totalInput = {
    set: ($eventValue: string) => {
      orderformService.setSubmitStatus({ reset: true });

      totalValue.value = $eventValue;
      const newValue = defineValueToSaveToOrderformServiceBasedOnInput($eventValue, { allowZero: true });

      if (newValue !== undefined) {
        orderformService.setTotal(newValue);
      }
    },
    onServiceChangeUpdate: (value: string | null) => {
      if (value !== totalValue.value) {
        totalValue.value = value;
      }
    },
  };

  return {
    stopPriceValue,
    limitPriceValue,
    amountValue,
    totalValue,
    stopPriceInput,
    limitPriceInput,
    amountInput,
    totalInput,
    v,
    validationErrorMessages,
    vuelidateReset,
    currentMinAmount,
    currentMinPrice,
    currentMaxPrice,
    currentMinAmountForMarketOrder,
    submitStatus,
    submitStatusMessage,
  };
};

const useOrderFormEmitterListener = () => {
  const orderFormEmitter = inject<OrderFormEmitterType>('orderFormEmitter');

  const setOrderFormResetListener = (onResetForm: () => void) => {
    orderFormEmitter?.on('reset-form-softly', onResetForm);

    onBeforeUnmount(() => {
      orderFormEmitter?.off('reset-form-softly', onResetForm);
    });
  };

  return {
    setOrderFormResetListener,
  };
};

const { t } = useI18n({ useScope: 'global' });

const { spotMarket } = toRefs(props);
const processingCreation = computed(() => orderformService.data.submitStatus.pending);
const feeValue = computed(() => orderformService.feeValue.value);

const { storedSide, storedType } = useOrderTypes();

const isBuyOrder = computed(() => storedSide.value === OrderSide.BUY);
const isLimitOrder = computed(() => storedType.value === OrderType.LIMIT);
const isStopOrder = computed(() => storedType.value === OrderType.STOP);
const isMarketOrder = computed(() => storedType.value === OrderType.MARKET);

const { setOrderFormResetListener } = useOrderFormEmitterListener();
const { accountStatus, accountStatusApproved } = accountService;
const { isLoadingLeverage, leverage: maxLeverage } = futuresService;
const { handleRiskyOrders } = useWarningModals(spotMarket, t('fundamentals.emptyValues.default'));

const { marketOrderMinAmount, currentMinPrice, currentMaxPrice } = useOrderFormPrice(spotMarket, isMarketOrder);
const {
  stopPriceValue,
  limitPriceValue,
  amountValue,
  totalValue,
  stopPriceInput,
  limitPriceInput,
  amountInput,
  totalInput,
  v,
  validationErrorMessages,
  vuelidateReset,
  submitStatusMessage,
} = useFormInputs(spotMarket, marketOrderMinAmount, currentMinPrice, currentMaxPrice);
const { showTooltips, resetShowTooltips, tooltipContent, tooltipFormatter } = useTooltips(spotMarket);
const { availableMaxAmountForCurrentSide, availableMaxAmountForPerps, amountToReceiveForCurrentSide, feeForCurrentSide, isMaxAmountAvailable, balanceBySide } =
  useOrderFormComputedHelpers(spotMarket, maxLeverage);

const marketOrdersSlippage = computed(() => settingsService.settings.userSettings.marketOrdersSlippage);
const orderAdvanced = computed(() => settingsService.settings.userSettings.orderAdvanced);
const stopPrice = computed(() => orderformService.data.stopPrice);
const limitPrice = computed(() => orderformService.data.limitPrice);
const amount = computed(() => orderformService.data.amount);
const total = computed(() => orderformService.data.total);
const maxLeverageDisplayValue = computed(() => (authService.hasBeenAuthenticated ? (maxLeverage.value ?? 0) : DEFAULT_MAX_LEVERAGE_LOGGED_OUT_DISPLAY_VALUE));

const { maxAmountForSlider, sliderIsDisabled, valueForSlider, marksForSlider, intervalForSlider } = useOrderFormSlider(
  spotMarket,
  isMarketOrder,
  isBuyOrder,
  amountValue,
  limitPriceValue,
  balanceBySide,
  availableMaxAmountForCurrentSide,
  availableMaxAmountForPerps,
  isMaxAmountAvailable,
  getOrderBook,
);

const submitText = computed(() => {
  if (!spotMarket.value) {
    return t('fundamentals.submit');
  }

  const symbol = spotMarket.value.base.code;

  if (props.isFutures) {
    return storedSide.value === OrderSide.BUY
      ? `${t('fundamentals.orders.side.buy')} / ${t('fundamentals.orders.side.long')} ${symbol}`
      : `${t('fundamentals.orders.side.sell')} / ${t('fundamentals.orders.side.short')} ${symbol}`;
  }
  return storedSide.value === OrderSide.BUY ? `${t('fundamentals.orders.side.buy')} ${symbol}` : `${t('fundamentals.orders.side.sell')} ${symbol}`;
});

const satisfiesPostOnlyCondition = computed(() => {
  if (spotMarket.value?.stateIs(MarketState.POST_ONLY)) {
    return isLimitOrder.value && orderAdvanced.value.enabled && orderAdvanced.value.timeInForce === TimeInForceType.POST_ONLY;
  }

  return true;
});

const isFormValid = computed(() => !v.value.$invalid && satisfiesPostOnlyCondition.value);

const prefillInputs = (isMarketChange = false) => {
  const { lastPrice, marketPrecision } = spotMarket.value;
  const limitPriceNumber = Number(limitPrice.value);

  /* prefill price */
  if (Number.isNaN(lastPrice)) {
    limitPriceValue.value = null;
    stopPriceValue.value = null;
    orderformService.setLimitPrice(null);
    orderformService.setStopPrice(null);
  } else if (isMarketChange || Number.isNaN(limitPriceNumber) || limitPriceNumber <= 0) {
    /** use market lastPrice */
    const lastPriceFixed = lastPrice.toFixed(marketPrecision);

    limitPriceValue.value = lastPriceFixed;
    stopPriceValue.value = lastPriceFixed;
    orderformService.setLimitPrice(lastPriceFixed);
    orderformService.setStopPrice(lastPriceFixed);
  } else if (!Number.isNaN(limitPriceNumber) && limitPriceNumber > 0) {
    /** use price from the store */
    limitPriceValue.value = limitPrice.value;
    stopPriceValue.value = stopPrice.value;
  } else {
    limitPriceValue.value = null;
    stopPriceValue.value = null;
    orderformService.setLimitPrice(null);
    orderformService.setStopPrice(null);
  }

  /* prefill amount */
  if (amount.value === null || (!Number.isNaN(Number(amount.value)) && Number(amount.value))) {
    amountValue.value = amount.value;
  }

  /* prefill total */
  if (total.value === null || (!Number.isNaN(Number(total.value)) && Number(total.value))) {
    totalValue.value = total.value;
  }
};

/*
const onMaxAmountClick = () => {
  amountInput.set(availableMaxAmountForCurrentSide.value.toFixed(spotMarket.value.amountPrecision));
};
*/

const sliderTooltipFormatter = tooltipFormatter(maxAmountForSlider);

const showToastOnFailure = () => orderformService.showToastOnFailure();

const waitForRiskyOrdersDone = async () => {
  const orderbook = await getOrderBook({
    marketPrecision: spotMarket.value.marketPrecision,
    marketId: spotMarket.value.id,
  }).catch((e) => {
    if (showToastOnFailure()) {
      displayDefaultFailedToast();
    }

    throw e;
  });

  return handleRiskyOrders(
    storedType.value,
    storedSide.value,
    orderbook,
    orderAdvanced.value.enabled && orderAdvanced.value.timeInForce === TimeInForceType.POST_ONLY,
    Number(amount.value),
    Number(limitPrice.value),
    Number(stopPrice.value),
  );
};

const softFormReset = () => {
  orderformService.softReset();
  nextTick(vuelidateReset);
};

const submit = async () => {
  if (orderformService.data.submitStatus.pending) {
    return;
  }

  orderformService.setSubmitStatus({
    submitData: {
      ...orderformService.data.submitStatus,
      pending: true,
    },
  });

  // TODO: Why is this needed??
  // try {
  //   await waitForRiskyOrdersDone();
  // } catch (e) {
  //   orderformService.setSubmitStatus({
  //     submitData: {
  //       ...orderformService.data.submitStatus,
  //       pending: false,
  //     },
  //   });

  //   return;
  // }

  if (v.value.$invalid) {
    v.value.$touch();
    orderformService.setSubmitStatus({
      submitData: {
        ...orderformService.data.submitStatus,
        pending: false,
      },
    });

    return;
  }

  orderformService.setSubmitStatus({
    submitData: {
      success: false,
      pending: true,
      code: '',
    },
  });

  try {
    const postOrderPayload = await orderformService.getPostOrderPayload({
      instrument: spotMarket.value.id,
      amount: amountValue.value,
      limitPrice: limitPriceValue.value,
      stopPrice: stopPriceValue.value,
      minPrice: currentMinPrice.value,
      maxPrice: currentMaxPrice.value,
    });

    const createWithRest = async () => {
      try {
        const data = await orderformService.postOrder.rest(postOrderPayload);

        if (data === null) {
          throw new Error('Server null error');
        }

        orderformService.handleOrderCreatedSuccess();
        nextTick(vuelidateReset);
      } catch (caughtError) {
        const { data: error } = caughtError as { data?: { error?: string } };

        orderformService.handleOrderCreationFailed(error?.error ?? '');
      }
    };

    await createWithRest();
  } catch (e) {
    logger.log('Order creation failed', e);
  }

  try {
    gaService.trackGAEvent(GACategory.ORDER, GAOrderAction.INITIATED, storedType.value.toLowerCase());
  } catch (e) {
    logger.log('Sending GA failed', e);
  }
};

const onInputBlur = ($event: FocusEvent) => {
  resetShowTooltips();

  // only reset validation when user decides to leave input empty
  // otherwise the required msg would be there until the next input change and that could be annoying

  if (!$event.target || ($event.target && !('name' in $event.target))) {
    return;
  }

  const { name } = $event.target;

  // @ts-ignore TODO fix it
  if (!$event.target.value && v.value[name]) {
    // @ts-ignore TODO fix it
    v.value[name].$reset();
  }
};
const onInputFocus = ($event: FocusEvent) => {
  if (!$event.target || ($event.target && !('name' in $event.target))) {
    return;
  }

  const { name } = $event.target;

  // @ts-ignore TODO fix it
  showTooltips[name] = true;

  orderformService.setSubmitStatus({ reset: true });
};

setOrderFormResetListener(softFormReset);

/** updating inputs based on corresponding "service" value changes */
watch(stopPrice, (vl) => {
  stopPriceInput.onServiceChangeUpdate(vl);
});
watch(limitPrice, (vl) => {
  limitPriceInput.onServiceChangeUpdate(vl);
});
watch(amount, (vl) => {
  amountInput.onServiceChangeUpdate(vl);
});
watch(total, (vl) => {
  totalInput.onServiceChangeUpdate(vl);
});

/** updating inputs and corresponding "service" value upon OF type change */
watch(storedType, () => prefillInputs());

/** resetting tooltips on scroll */
watch(() => props.psScrollTriggered, resetShowTooltips);

/** updating inputs and corresponding "service" value upon market change */
watch(
  () => spotMarket.value.id,
  () => {
    prefillInputs(true);
    vuelidateReset();
  },
  { immediate: true },
);

/**
 * updating inputs and corresponding "service" value upon lastPrice change;
 * on non modal forms we want to update inputs only once (upon initial exchange load when lastPrice is received)
 * */
watch(
  () => spotMarket.value.lastPrice,
  (_newLastPrice: number | undefined, oldLastPrice: number | undefined) => {
    if (Number.isNaN(Number(oldLastPrice))) {
      prefillInputs();
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => orderformService.hardReset());
</script>

<template>
  <form
    ref="form"
    class="order-form"
    autocomplete="off"
    @submit.prevent="submit"
  >
    <!-- Inputs -->
    <div
      class="order-form__inputs"
      testid="order-form-inputs"
    >
      <div
        v-if="isFutures"
        class="order-form__leverage"
      >
        {{ $t('fundamentals.leverage') }}
        <ot-spinner
          v-if="isLoadingLeverage"
          class="order-form__leverage__spinner"
        />
        <ot-lozenge
          v-else
          :text="`${maxLeverageDisplayValue}x`"
          :grayscale="true"
          class="order-form__leverage__lozenge"
        />
      </div>
      <!-- Limit Price -->
      <div
        v-if="isLimitOrder || isStopOrder"
        class="order-form__input-wrapper"
      >
        <ot-input-error-wrapper
          :no-message-placeholder="true"
          :error="v.limitPriceValue"
          :error-messages="validationErrorMessages.limitPriceValue"
        >
          <x-input-number
            v-model:value="limitPriceValue"
            v-tooltip="tooltipContent(limitPriceValue, 'limitPriceValue')"
            name="limitPriceValue"
            :label="$t('modules.orderForm.limitPrice')"
            :state="v.limitPriceValue.$error ? 'error' : 'default'"
            variant="dark"
            :compact="true"
            :precision="spotMarket.marketPrecision"
            :currency="spotMarket.quote.code"
            :lpignore="true"
            testid="order-limit-price"
            @focus="onInputFocus"
            @blur="onInputBlur"
            @update:value="limitPriceInput.set($event)"
          />
        </ot-input-error-wrapper>
      </div>
      <!-- Amount -->
      <div class="order-form__input-wrapper">
        <ot-input-error-wrapper
          :error="v.amountValue"
          :error-messages="validationErrorMessages.amountValue"
        >
          <order-size-input
            v-model:amount-value="amountValue"
            v-model:total-value="totalValue"
            :autofocus="false"
            :amount-tooltip="tooltipContent(amountValue, 'amountValue')"
            :total-tooltip="tooltipContent(totalValue, 'totalValue')"
            :market="spotMarket"
            :input-state="v.amountValue.$error ? 'error' : 'default'"
            :order-side="storedSide"
            @update:amount-value="amountInput.set($event)"
            @update:total-value="totalInput.set($event)"
            @input-blur="onInputBlur"
            @input-focus="onInputFocus"
          />
        </ot-input-error-wrapper>
        <order-form-balance
          :balance="balanceBySide"
          :show-loading="balanceLoadingUntil"
          :is-futures="isFutures"
          :max-position-size="availableMaxAmountForPerps"
        />
        <x-slider
          class="order-form__slider"
          :model-value="valueForSlider"
          :color-variant="isBuyOrder ? 'success' : 'error'"
          :min="0"
          :max="maxAmountForSlider"
          :marks="marksForSlider"
          :interval="intervalForSlider"
          :fit-tooltip-max-value="fitSliderTooltipMaxValue && valueForSlider === maxAmountForSlider"
          :silent="true"
          :step-style="{ width: '6px', height: '6px', top: '-1px', left: '-1px' }"
          :duration="0.2"
          :tooltip-formatter="sliderTooltipFormatter"
          :disabled="sliderIsDisabled"
          :drag-on-click="true"
          @change="amountInput.set($event.toString())"
        />
      </div>
    </div>

    <!-- Setting -->
    <order-form-settings
      v-if="isLimitOrder"
      :market="spotMarket"
    />
    <!-- Market order specific -->
    <template v-if="isMarketOrder">
      <div
        v-if="isMarketOrder"
        testid="market-total-hint"
        class="order-form__market-total-hint"
      >
        {{ $t('modules.orderForm.marketOrderTotalHint') }}
      </div>
      <i18n-t
        keypath="modules.orderForm.marketOrderSlippage.text"
        tag="div"
        scope="global"
        class="order-form__market-total-hint"
      >
        <template #linkText>
          <ot-link-internal
            :to="{ name: 'settings.general' }"
            :title="$t('modules.orderForm.marketOrderSlippage.linkText')"
            testid="market-order-slippage-link"
          />
        </template>
        <template #value>
          <span testid="market-order-slippage-percent">{{ `${marketOrdersSlippage}%` }}</span>
        </template>
      </i18n-t>
    </template>
    <!-- Fees -->
    <order-form-fees
      class="order-form__fees"
      :side="storedSide"
      :type="storedType"
      :fee-value="feeValue"
      :side-fee="feeForCurrentSide"
      :side-amount="amountToReceiveForCurrentSide"
    />
    <!-- Submit -->
    <x-button
      v-if="accountStatusApproved || !accountStatus"
      class="order-form__submit"
      :class="{ 'order-form__submit--active': isFormValid }"
      :disabled="!isFormValid"
      :loading="processingCreation"
      :text="submitText"
      :variant="isBuyOrder ? 'success' : 'error'"
      size="md"
      testid="submit-order"
      type="submit"
    />
    <!-- Errors section -->
    <order-form-status-message
      v-if="!accountStatusApproved && accountStatus"
      :message="$t(`banners.user.${accountStatus}`)"
    />
    <order-form-status-message
      v-if="submitStatusMessage"
      class="order-form__status-message"
      :message="submitStatusMessage"
      testid="order-form-status-msg"
    />
    <order-form-status-message
      v-if="spotMarket.state === 'POST_ONLY'"
      class="order-form__post-only-message"
      :message="$t('modules.orderForm.postOnlyMessage')"
    />
  </form>
</template>

<style lang="scss">
.order-form {
  display: flex;
  height: auto;
  flex: 1 0 auto;
  flex-direction: column;
  color: rgb(var(--v-theme-text-secondary));
  font-size: var(--font-size-sm);

  &__input-wrapper {
    margin-bottom: 10px;
  }

  &__balances {
    margin-bottom: 15px;

    .xs & {
      margin-bottom: 18px;
    }
  }

  &__slider {
    margin: 0 8px 24px;
  }

  &__fees {
    margin: 16px 0;
  }

  &__submit {
    margin-bottom: 16px;

    &--active {
      font-weight: var(--font-weight-medium);
    }
  }

  &__leverage {
    display: flex;
    align-items: center;
    gap: 4px;
    margin-bottom: 20px;
    font-size: var(--font-size-lg);

    &__lozenge {
      border-color: rgb(var(--v-theme-elevation-2));
      background-color: rgb(var(--v-theme-elevation-2));
      font-size: var(--font-size-sm);
    }

    &__spinner {
      display: inline-flex;
      min-width: 30px;
      max-width: 30px;
    }
  }

  &__market-total-hint {
    color: rgb(var(--v-theme-text-tertiary));
    line-height: var(--app-line-height-small);
  }

  &__post-only-message,
  &__status-message {
    margin-bottom: 16px;
  }

  .xs &__status-message {
    margin-bottom: 16px;
  }

  .xs &,
  .sm &,
  .md & {
    padding: 0;
    background-color: transparent;
  }
}
</style>
